import { useMemo, useState } from 'react';
import { messagePreview, type Message } from '@machs/shared';
import { useSync } from '../lib/sync';
import { imageUrl } from '../lib/images';
import { Icon } from '../lib/icons';
import { avatarGradient, initials } from '../lib/format';
import { Avatar, Dialog, useToast } from './ui';

/**
 * Nachricht in andere Aufgaben weiterleiten (wie im Messenger: mehrere Ziele
 * auf einmal). Offene und zuletzt aktive Aufgaben stehen oben.
 */
export function ForwardDialog({ message, onClose }: { message: Message; onClose: () => void }) {
  const { client, version } = useSync();
  const toast = useToast();
  const [suche, setSuche] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const tasks = useMemo(() => {
    const q = suche.trim().toLowerCase();
    return [...client.data.task.values()]
      .filter((t) => t.id !== message.taskId)
      .filter((t) => {
        if (!q) return true;
        const project = client.data.project.get(t.projectId);
        return t.title.toLowerCase().includes(q) || !!project?.name.toLowerCase().includes(q);
      })
      .sort((a, b) => Number(a.status === 'erledigt') - Number(b.status === 'erledigt') || (b.lastMessageAt ?? b.createdAt).localeCompare(a.lastMessageAt ?? a.createdAt))
      .slice(0, 60);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, version, suche, message.taskId]);

  const media = message.mediaId ? client.data.media.get(message.mediaId) ?? null : null;
  const vorschau = message.body.trim() || messagePreview(message.kind, '', media);

  async function weiterleiten() {
    if (!picked.length || busy) return;
    setBusy(true);
    try {
      await client.forwardMessage(message.id, picked);
      toast(picked.length === 1 ? 'Weitergeleitet' : `An ${picked.length} Aufgaben weitergeleitet`);
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title="Weiterleiten" onClose={onClose}>
      <div className="flex flex-col gap-3">
        <div className="quote m-0">
          <b className="block text-[var(--text)]">{message.authorName}</b>
          <span className="line-clamp-2">{vorschau}</span>
        </div>
        <input className="input" value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Aufgabe oder Projekt suchen" aria-label="Aufgabe suchen" />
        <ul className="fwdlist">
          {tasks.map((t) => {
            const project = client.data.project.get(t.projectId);
            const on = picked.includes(t.id);
            return (
              <li key={t.id}>
                <button aria-pressed={on} onClick={() => setPicked(on ? picked.filter((x) => x !== t.id) : [...picked, t.id])}>
                  <Avatar name={initials(project?.name ?? t.title)} gradient={project?.color ?? avatarGradient(project?.id ?? t.id)} src={imageUrl(project?.imageMediaId)} size={34} square />
                  <span className="min-w-0 flex-1">
                    <b className="block truncate text-[14px] font-medium">{t.title}</b>
                    <small className="block truncate text-[12px] text-[var(--muted)]">{project?.name}{t.status === 'erledigt' ? ' · erledigt' : ''}</small>
                  </span>
                  <span className={`fwdcheck ${on ? 'on' : ''}`}>{on && <Icon name="check" size={14} />}</span>
                </button>
              </li>
            );
          })}
          {tasks.length === 0 && <li className="py-6 text-center text-[13px] text-[var(--muted)]">Keine passende Aufgabe.</li>}
        </ul>
        <div className="flex justify-end gap-2">
          <button className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary" disabled={!picked.length || busy} onClick={() => void weiterleiten()}>
            <Icon name="forward" size={17} />
            {picked.length > 1 ? `An ${picked.length} weiterleiten` : 'Weiterleiten'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
